import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { adminApi } from "../../../../../../../services/Api/Admin/adminApi";
import { ProductPackageApi } from "../../../services/types";
import { BarcodesTable } from "./BarcodesTable";

interface Props {
	productPackage?: ProductPackageApi;
}

export const ProductPackagesForm = ({ productPackage }: Props) => {
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const history = useHistory();
	const form = useForm<ProductPackageApi>({
		criteriaMode: "all",
		defaultValues: productPackage,
	});
	const {
		formState,
		formState: { errors },
		register,
		handleSubmit,
		control,
		reset,
		setError,
	} = form;
	const measureTypeOptions = ["ITEM", "KILOGRAM", "LITER", "PACKAGE"].map((measureType) => ({
		value: measureType,
		label: t(`enums.products.measure_types.${measureType}`),
	}));

	const onSubmit = handleSubmit(async (data: ProductPackageApi) => {
		try {
			setLoading(true);
			if (productPackage?.id) {
				const params = { include: "barcodes" };
				const res = await adminApi.updateProductPackage(productPackage.id.toString(), data, params);
				reset(res);
			} else {
				const { id: productPackageId } = await adminApi.createProductPackage(data);
				history.push(`/admin/product_packages/${[productPackageId]}/edit`);
			}
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		} finally {
			setLoading(false);
		}
	});

	return (
		<FormDirty formState={formState} onSubmit={onSubmit} loading={loading}>
			<div className="row">
				<div className="col-md-6">
					<FormInput
						name="name"
						label={t("common.word.name", { ns: "lib" })}
						errors={errors}
						register={register}
					/>
				</div>
				<div className="col-md-6">
					<FormSelectGroup
						name="measure_type"
						label={t("common.word.measure_type")}
						errors={errors}
						control={control}
						options={measureTypeOptions}
					/>
				</div>
			</div>
			<div className="row">
				<div className="col-md-6">
					<FormNumberInput
						name="specific_weight"
						control={control}
						errors={errors}
						suffix=""
						label={t("modules.product_package.field.specific_weight.title")}
						decimalScale={3}
					/>
				</div>
			</div>
			<BarcodesTable form={form} />
		</FormDirty>
	);
};
